import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";

const PROJECTS = [
  {
    id: "p1",
    cat: { en: "Commercial", es: "Comercial" },
    title: { en: "Lunaire — Brand Film", es: "Lunaire — Brand Film" },
    img: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "A 60-second brand film for a luxury fashion house — directed, shot and color-graded by Sue Production.",
      es: "Una pieza de marca de 60 segundos para una casa de moda de lujo — dirigida, grabada y con color por Sue Production.",
    },
  },
  {
    id: "p2",
    cat: { en: "Personal Brand", es: "Marca Personal" },
    title: { en: "Marcus Reed — Investor Brand", es: "Marcus Reed — Marca Inversionista" },
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "A year-long content engine that built Marcus Reed into a recognized voice in capital markets.",
      es: "Un motor de contenido anual que convirtió a Marcus Reed en una voz reconocida en mercados de capital.",
    },
  },
  {
    id: "p3",
    cat: { en: "Social", es: "Social" },
    title: { en: "Kapoor Studio — Social Campaign", es: "Kapoor Studio — Campaña Social" },
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "Twelve weeks of TikTok and Reels native production — 9.4M organic views.",
      es: "Doce semanas de producción nativa de TikTok y Reels — 9.4M de vistas orgánicas.",
    },
  },
  {
    id: "p4",
    cat: { en: "AI", es: "IA" },
    title: { en: "Atelier 9 — AI Lookbook", es: "Atelier 9 — Lookbook IA" },
    img: "https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "A full lookbook produced with AI imagery and live action plates — concept to delivery in 14 days.",
      es: "Lookbook completo con imagen IA y placas de acción real — del concepto a la entrega en 14 días.",
    },
  },
  {
    id: "p5",
    cat: { en: "Commercial", es: "Comercial" },
    title: { en: "Stratos — Performance Ads", es: "Stratos — Ads de Performance" },
    img: "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "A 24-variant performance creative system that lifted CPA by 38% in 60 days.",
      es: "Sistema creativo de performance con 24 variantes que redujo el CPA un 38% en 60 días.",
    },
  },
  {
    id: "p6",
    cat: { en: "Personal Brand", es: "Marca Personal" },
    title: { en: "Sofía Marín — CMO Series", es: "Sofía Marín — Serie CMO" },
    img: "https://images.unsplash.com/photo-1521119989659-a83eee488004?auto=format&fit=crop&w=1600&q=85",
    desc: {
      en: "A LinkedIn-first video series that positioned a marketing leader as a top voice.",
      es: "Serie de video orientada a LinkedIn que posicionó a una líder de marketing como top voice.",
    },
  },
];

export default function PortfolioSection() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState("All");
  const [open, setOpen] = useState(null);

  const filtered = PROJECTS.filter((p) => {
    if (active === "All" || active === "Todos") return true;
    return p.cat[lang] === active || p.cat.en === active;
  });

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-lux" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-lux">
            {t.portfolio.label}
          </span>
        </motion.div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl text-balance"
          >
            {t.portfolio.title}
          </motion.h2>
          <p className="text-white/55 max-w-md text-[15px]">{t.portfolio.subtitle}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {t.portfolio.categories.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c}`}
              onClick={() => setActive(c)}
              className={`text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-all duration-300 ${
                active === c
                  ? "border-white bg-white text-black"
                  : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                key={p.id}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                onClick={() => setOpen(p)}
                data-testid={`portfolio-item-${p.id}`}
                className="group relative aspect-[4/5] overflow-hidden bg-zinc-900 text-left"
              >
                <img
                  src={p.img}
                  alt={p.title[lang]}
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute inset-0 border border-white/0 group-hover:border-white/20 transition-colors duration-700" />
                <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.3em] text-white/70 luxury-glass px-3 py-1 rounded-full">
                  {p.cat[lang]}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex items-end justify-between">
                  <h3 className="font-display text-xl md:text-2xl font-light tracking-tight text-white">
                    {p.title[lang]}
                  </h3>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/60 group-hover:text-white transition-colors">
                    {t.portfolio.open}
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent
          data-testid="portfolio-lightbox"
          className="max-w-5xl w-[95vw] bg-black border-white/10 p-0 overflow-hidden"
        >
          {open && (
            <div className="relative">
              <img src={open.img} alt={open.title[lang]} className="w-full h-[60vh] object-cover" />
              <button
                onClick={() => setOpen(null)}
                className="absolute top-4 right-4 luxury-glass rounded-full p-2 hover:bg-white hover:text-black transition-colors"
              >
                <X size={18} />
              </button>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                  {open.cat[lang]}
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-light tracking-tighter mb-4">
                  {open.title[lang]}
                </h3>
                <p className="text-white/60 max-w-2xl">{open.desc[lang]}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
